import { Link, usePage } from "@inertiajs/react"
import { Calendar, LogOut, User } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { NotificationBell } from "@/components/notification-bell"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import {
  availability_windows_path,
  browse_path,
  dashboard_path,
  destroy_user_session_path,
  edit_profile_path,
  matches_path,
  my_skills_path,
  root_path,
  sessions_path,
} from "@/routes"
import type { User as UserType } from "@/types"

const NAV_LINKS = [
  { label: "Home", href: dashboard_path() },
  { label: "My Skills", href: my_skills_path() },
  { label: "Matches", href: matches_path() },
  { label: "Sessions", href: sessions_path() },
  { label: "Browse", href: browse_path() },
]

function initials(name: string | null, email: string) {
  if (!name) return email[0]?.toUpperCase() ?? "?"
  return name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

export function Navbar({ user }: { user: UserType }) {
  const currentUrl = usePage().url

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-[color-mix(in_oklch,var(--background),transparent_18%)] backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <Link href={root_path()} className="font-heading text-lg font-extrabold text-foreground">
          Swapaz <span className="text-brand-accent">•</span>
        </Link>

        <nav className="flex items-center gap-8">
          {NAV_LINKS.map((link) => {
            const isActive = currentUrl.startsWith(link.href)
            return (
              <Link
                key={link.label}
                href={link.href}
                className={cn(
                  "text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground",
                  isActive && "text-foreground underline decoration-2 underline-offset-8 decoration-brand-accent"
                )}
              >
                {link.label}
              </Link>
            )
          })}
        </nav>

        <div className="flex items-center gap-2">
          <NotificationBell />
          <Popover>
            <PopoverTrigger
              aria-label="Account menu"
              className="rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              <Avatar>
                <AvatarImage src={user.avatar_url ?? undefined} />
                <AvatarFallback>{initials(user.name, user.email)}</AvatarFallback>
              </Avatar>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1">
              <Link
                href={edit_profile_path()}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
              >
                <User className="size-4" /> Profile
              </Link>
              <Link
                href={availability_windows_path()}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
              >
                <Calendar className="size-4" /> Availability
              </Link>
              <Link
                href={destroy_user_session_path()}
                method="delete"
                as="button"
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10"
              >
                <LogOut className="size-4" /> Log out
              </Link>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  )
}
